import { useEffect, useRef, useState } from 'react'
import type { LDClient } from 'launchdarkly-js-client-sdk'
import { getAnonymousId, segmentTrack } from '@src/functions/segment'
import { getLDContext, isCrawler, LAUNCHDARKLY_CLIENT_ID } from '@src/functions/featureFlags'

/**
 * How long to wait for LaunchDarkly before giving up and leaving the flag at its
 * fallback (seconds). This is the CLS guard: a flag resolving several seconds late
 * would swap above-the-fold content long after the page looked settled, so past
 * this point the visitor stays on the control for the rest of the page view.
 */
const INIT_TIMEOUT_SECONDS = 3

export type ExperimentVariant = {
  /** The flag's value. Stays at `fallback` until LaunchDarkly actually resolves. */
  enabled: boolean
  /**
   * Whether LaunchDarkly answered. Never true on the server, on the first client
   * render, or when LD is missing, blocked, or times out.
   */
  resolved: boolean
}

/**
 * Reads one boolean LaunchDarkly flag in the browser and reports the resulting
 * variant to Amplitude once per page view as an `Experiment Viewed` event.
 *
 * **Why the SDK is imported dynamically rather than through a React provider.**
 * `launchdarkly-react-client-sdk`'s `LDProvider` has to be imported at module scope
 * to wrap the page, which puts ~18 kB of LaunchDarkly into the *initial* JS of every
 * page that uses it — measured: `/paint-disposal` went from 6 kB to 24 kB. That is a
 * Core Web Vitals cost on our top organic landing pages, which is the thing the
 * current SEO work is trying to protect. Importing the plain JS client inside an
 * effect puts it in a lazy chunk instead, and is the same trick `N8nChatEmbed` uses
 * for `@n8n/chat`. It also sidesteps SSR entirely, so server-rendered HTML and first
 * paint are always the control variant — including the static HTML Googlebot indexes.
 *
 * **Why the exposure event matters.** It is the only way this test can be read. The
 * treatment converts through n8n, which posts completions straight to the backend, so
 * the browser never sees a conversion (see docs_ignore/N8N_CHAT_FRONTEND_PLAN.md,
 * "Client-side conversion tracking is not possible in v1"). Control converts through
 * the quote form. The two funnels are joined downstream on Segment's `anonymousId`,
 * which this event carries and which the chat also sends to n8n in its metadata.
 *
 * Flags are read once at page load — streaming is off, so a kill switch flipped in
 * LaunchDarkly takes effect on subsequent page loads rather than mid-session. That is
 * the deliberate trade: an open streaming connection per anonymous visitor is a lot of
 * cost and LaunchDarkly billable activity for a marketing page.
 *
 * Pass `{ skip: true }` when the caller has already decided the variant without the
 * flag (see `IS_LOCAL_DEV`): the hook then stays at `fallback`, never contacts
 * LaunchDarkly, and sends no exposure event.
 */
export function useExperimentVariant(
  flagKey: string,
  fallback = false,
  options: { skip?: boolean } = {},
): ExperimentVariant {
  const { skip = false } = options
  const [variant, setVariant] = useState<ExperimentVariant>({
    enabled: fallback,
    resolved: false,
  })
  // Exposure is one event per page view, not one per re-render.
  const trackedRef = useRef(false)

  useEffect(() => {
    // A caller that has already decided the variant for itself — local development
    // forces the chat on — must not evaluate the flag: that would spend a billable
    // LaunchDarkly context and file an `Experiment Viewed` event for a variant the
    // visitor is not actually being shown, which is worse than no exposure at all.
    if (skip) return undefined

    // Crawlers and synthetic agents never reach LaunchDarkly at all: they stay on the
    // control, the DOM Googlebot renders stays stable on a page we're trying to rank,
    // they stay out of the experiment numbers, and — since LD bills client-side usage
    // by monthly active contexts — they stay off the bill. The `crawler` context
    // attribute is still sent for the ones that slip past this check, so a targeting
    // rule in LaunchDarkly can catch them too.
    if (!LAUNCHDARKLY_CLIENT_ID || isCrawler()) return undefined
    let cancelled = false
    let client: LDClient | undefined

    import('launchdarkly-js-client-sdk')
      .then(({ initialize }) => {
        if (cancelled) return undefined
        client = initialize(LAUNCHDARKLY_CLIENT_ID, getLDContext(), {
          streaming: false,
          // Nothing on this site reads flags outside a variation() call, and the
          // extra allFlags events are pure billable noise.
          sendEventsOnlyForVariation: true,
        })
        return client.waitForInitialization(INIT_TIMEOUT_SECONDS).then(() => {
          if (cancelled || !client) return
          const enabled = client.variation(flagKey, fallback) === true
          setVariant({ enabled, resolved: true })
          if (trackedRef.current) return
          trackedRef.current = true
          segmentTrack('Experiment Viewed', {
            experimentKey: flagKey,
            variant: enabled ? 'treatment' : 'control',
            anonymousId: getAnonymousId(),
          })
        })
      })
      .catch(() => {
        // Timed out, blocked by an ad blocker, or misconfigured. Leave the visitor
        // on the fallback and send nothing — an exposure we can't trust is worse
        // than no exposure at all.
      })

    return () => {
      cancelled = true
      // Flushes any queued analytics events before tearing the client down.
      client?.close()
    }
  }, [flagKey, fallback, skip])

  return variant
}

export default useExperimentVariant
